<template>
  <div
    @mouseenter="$emit('hovered')"
    @mouseleave="$emit('unhovered')"
    class="product-card"
    :class="{
      'product-card--not--adaptive': notAdaptive,
      'product-card--open': opened,
      'product-card--slider': slider,
      'product-card--is-touch': isTouch,
      'product-card--adaptive-main-page': adaptiveMainPage,
      'product-card--simple-card': simpleCard
    }"
    :data-productKey="code"
  >
    <div
      ref="productCard"
      class="product-card__full">
      <div class="product-card__info">
        <div class="product-card__height">
          <div class="product-card__photo-wrapper">
            <a :href="link" class="product-card__gallery-link">
              <ProductCardImageSlider class="product-card__gallery" :images="images" />
            </a>
            <ProductBottomBadges
              :data="bottomBadge"
              class="product-card__bottom-badges" />
          </div>

          <div class="product-card__reviews-wrap">
            <div class="product-card__reviews">
              <StarRating
                v-if="rating"
                :value="Math.round(rating)"
                :disabled="true"
                class="product-card__rating"
                :title="Number(rating).toFixed(1)"
              />
              <ReviewsCounter
                :product-link="link"
                :value="reviewsCount" />
            </div>
          </div>

          <div class="product-card__code">
            {{ $t('product_code') + ' ' + code }}
            <CopyToClipboardButton class="product-card__code-copy"
                                   :text-for-copy="code"
            >
              <svg data-v-427a7248=""
                   width="18"
                   height="18"
                   viewBox="0 0 18 18"
                   fill="none"
                   xmlns="http://www.w3.org/2000/svg">
                <path data-v-427a7248=""
                      d="M11.25 11.25H13.35C14.1901 11.25 14.61 11.25 14.9308 11.0865C15.2131 10.9427 15.4429 10.7133 15.5867 10.431C15.7502 10.1102 15.7502 9.69012 15.7502 8.85004V4.65002C15.7502 3.80994 15.7502 3.3899 15.5867 3.06903C15.4429 2.78679 15.2131 2.5573 14.9308 2.41349C14.61 2.25 14.1902 2.25 13.3501 2.25H9.15015C8.31007 2.25 7.88972 2.25 7.56885 2.41349C7.2866 2.5573 7.0573 2.78679 6.91349 3.06903C6.75 3.3899 6.75 3.80998 6.75 4.65005V6.75005M2.25 13.3501V9.15005C2.25 8.30998 2.25 7.8899 2.41349 7.56903C2.5573 7.28679 2.7866 7.0573 3.06885 6.91349C3.38972 6.75 3.81007 6.75 4.65015 6.75H8.85015C9.69023 6.75 10.11 6.75 10.4308 6.91349C10.7131 7.0573 10.9429 7.28679 11.0867 7.56903C11.2502 7.8899 11.2502 8.30994 11.2502 9.15002V13.35C11.2502 14.1901 11.2502 14.6102 11.0867 14.931C10.9429 15.2133 10.7131 15.4427 10.4308 15.5865C10.11 15.75 9.69023 15.75 8.85015 15.75H4.65015C3.81007 15.75 3.38972 15.75 3.06885 15.5865C2.7866 15.4427 2.5573 15.2133 2.41349 14.931C2.25 14.6102 2.25 14.1901 2.25 13.3501Z"
                      stroke="#909090"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"></path></svg>
            </CopyToClipboardButton>
          </div>

          <ProductTitle
            @click="handleRedirect"
            :title="title"
            :link="link"
            class="product-card__title" />
          <span
            class="product-card__arrival-date-employee"
            v-if="arrivalDateEmployee && isManager"
            v-html="arrivalDateEmployee"
          ></span>
        </div>
        <div class="product-card__price-actions">
          <div v-if="showProductPrice" class="product-card__price-wrap">
            <ProductPrice
              :type="adaptiveMainPage ? 'adaptive-main-page' : simpleCard ? 'simple-card' : null"
              :price="getNewPrice"
              :old-price="getOldPrice"
              :badges="parsedBadges"
              class="product-card__price"
              :vertical="true"
            >
              <template #after-old-price>
                <ProductCardPriceDiff
                  :data="getPriceDiff"
                  class="product-card__price-diff"
                />
              </template>
            </ProductPrice>
          </div>
          <span
            v-else-if="!isPreorder"
            class="product-card__price product-card__price--not-available"
            :class="{ 'product-card__price--not-available-arrival': arrivalDate }"
          >{{ arrivalDate || $t('out_of_stock') }}</span
          >
          <span
            v-else
            class="product-card__price product-card__price--pre-order--available">{{
              arrivalDate || ''
            }}</span>
          <BuyBtn
            v-if="isBuyButtonVisible && adaptiveMainPage || simpleCard"
            :title-analytics="titleAnalytics"
            :product-id="id"
            :code="code"
            :price="getNewPrice"
            :store-id="availableInStoreWithQuantity ? availableStoreId : ''"
            :available-in-store="availableInStoreWithQuantity"
            :esputnik-data="esputnikData"
            :esputnik-block-id="esutnikBlockId"
            :stores-data="availableInStore"
            :product-link="link"
            class="product-card__buy-btn"
            type="icon-only"
          />
          <WaitListBtn
            v-else
            :in-wait-list-status="isInWaitList"
            :title-analytics="titleAnalytics"
            :product-id="id"
            :is-small-card="true"
            class="wait-list--card product-card__wait-list"
          />
        </div>
        <div class="product-card__actions">
          <a
            class="product-card__pseudo-btn"
            v-if="withoutBuy"
            :href="link">
            {{ $t('product_show_detail_title') }}
          </a>
          <BuyBtn
            v-else-if="isBuyButtonVisible"
            :title-analytics="titleAnalytics"
            :product-id="id"
            :code="code"
            :price="getNewPrice"
            :store-id="availableInStoreWithQuantity ? availableStoreId : ''"
            :esputnik-data="esputnikData"
            :esputnik-block-id="esutnikBlockId"
            :available-in-store="availableInStoreWithQuantity"
            :product-link="link"
            :stores-data="availableInStore"
            class="product-card__buy-btn"
          />
          <BuyBtn
            v-else-if="isPreorder"
            pre-order-btn
            :title-analytics="titleAnalytics"
            :title="title"
            :price-old="getOldPrice"
            :photo="imageUrl"
            :product-id="id"
            :code="code"
            :price="getNewPrice"
            class="product-card__buy-btn"
            :class="{ 'product-card__buy-btn--novelty': isPreorder }"
          />
          <WishListBtn
            v-if="!isWaitlistPage"
            class="product-card__wish-list-btn"
            :id="id"
            :in-wish-list-status="isInWishList"
            :analytics-data="analyticsData"
            :code="code"
            @mouseenter.native="handlePhoto($event, true)"
            @mouseleave.native="handlePhoto($event, false)"
          />
          <AddToComparisonBtn
            v-if="!isWaitlistPage"
            :product-id="id"
            :title-analytics="titleAnalytics"
            class="product-card__add-to-comparison"
            @mouseenter.native="handlePhoto($event, true)"
            @mouseleave.native="handlePhoto($event, false)"
          />
          <WaitListCardMenu
            v-if="isWaitlistPage"
            :product-data="{ ...$props }"
            :is-in-wish-list="isInWishList"
            @clickWishlist="addToWishList"
            @clickRemove="removeFromWaitList"
            class="product-card__wait-list-btn"
          />
        </div>
        <ProductOptions
          class="product-card__options"
          :link="link"
          v-if="options.length"
          is-product-card
          :options="options" />
        <ProductCardCharacteristics
          v-if="characteristics.length"
          :values="characteristics ? characteristics : []"
          class="product-card__characteristics"
        />
        <a
          v-if="characteristics.length"
          :href="getCharacteristicTabUrl"
          class="product-card__characteristics-link">
          {{ $t('product-card-characteristics-link') }} >
        </a>
      </div>
      <ProductBadges
        v-if="computedBadges.length"
        class="product-card__badges"
        :class="{ 'product-badges--not--adaptive': notAdaptive }"
        :values="computedBadges"
      />
    </div>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { isTouch } from '../../../mixins/isTouchDevice'
import ProductPrice from '../ProductPrice'
import StarRating from '../StarRating'
import ReviewsCounter from '../ReviewsCounter'
import WishListBtn from '../WishListBtn'
import ProductCardCharacteristics from '../ProductCardCharacteristics'
import ProductBadges from '../ProductBadges'
import LazyLoadDirective from '../../../directives/LazyLoadDirective'
import ProductOptions from '../product-options/ProductOptions'
// import ProductPhoto from '../ProductPhoto'
import ProductTitle from '../ProductTitle'
import BuyBtn from '../../cart/BuyBtn'
import AddToComparisonBtn from '../product-comparison/AddToComparisonBtn'
import WaitListBtn from '../product-page/WaitListBtn'
import ProductBottomBadges from '../ProductBottomBadges'
import { GoogleTagManagerService, GoogleEnhancedEcommerce } from '@/services/AnalyticService'
import { getProductMicrodata } from '@/helpers/microdata/getProductMicrodata'
import WaitListCardMenu from '@/components/product/product-card/WaitListCardMenu'
import ProductCardPriceDiff from '@/components/product/product-card/ProductCardPriceDiff'
import ProductCardImageSlider from './ProductCardImageSlider.vue'
import CopyToClipboardButton from '@/components/helpers/CopyToClipboardButton'

export default {
  name: 'ProductCard',
  mixins: [isTouch],
  components: {
    ProductTitle,
    // ProductPhoto,
    ProductOptions,
    ProductPrice,
    StarRating,
    ReviewsCounter,
    WishListBtn,
    ProductCardCharacteristics,
    ProductBadges,
    BuyBtn,
    AddToComparisonBtn,
    WaitListBtn,
    ProductBottomBadges,
    WaitListCardMenu,
    ProductCardPriceDiff,
    ProductCardImageSlider,
    CopyToClipboardButton
  },
  props: {
    categoryBlockTitle: {
      type: String,
      default: ''
    },
    categoryBlockId: {
      type: Number,
      default: null
    },
    position: {
      type: Number,
      default: null
    },
    id: {
      type: Number,
      default: null
    },
    code: {
      type: String,
      default: ''
    },
    imageUrl: {
      type: String,
      required: false,
      default: ''
    },
    secondImageUrl: {
      type: String,
      required: false,
      default: ''
    },
    gallery: {
      type: Array,
      default: () => []
    },
    mobileImageUrl: {
      type: String,
      default: ''
    },
    link: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    titleAnalytics: {
      type: String,
      default: null
    },
    categoryId: {
      type: Number,
      default: null
    },
    categoryParentId: {
      type: Number,
      default: null
    },
    rating: {
      type: String,
      default: ''
    },
    reviewsCount: {
      type: Number,
      default: null
    },
    price: {
      type: String,
      default: ''
    },
    characteristics: {
      type: Array
    },
    badges: {
      type: [Array, String],
      default: () => []
    },
    isAvailable: {
      type: Boolean,
      default: false
    },
    isPreorder: {
      type: Boolean,
      default: false
    },
    isNovelty: {
      type: Boolean,
      defaultL: false
    },
    isInWaitList: {
      type: Boolean,
      default: false
    },
    isInWishList: {
      type: Boolean,
      default: false
    },
    notAdaptive: {
      type: Boolean,
      default: false
    },
    adaptiveMainPage: {
      type: Boolean,
      default: false
    },
    opened: {
      type: Boolean,
      default: false
    },
    slider: {
      type: Boolean,
      default: false
    },
    block: {
      type: String,
      default: ''
    },
    simpleCard: {
      type: Boolean,
      default: false
    },
    options: {
      type: Array,
      default: () => []
    },
    bottomBadge: {
      type: Array,
      default: () => []
    },
    withoutBuy: {
      type: Boolean,
      default: false
    },
    inStock: {
      type: Boolean,
      default: undefined
    },
    arrivalDate: [String, Boolean],
    arrivalDateEmployee: [String, Boolean],
    has360: {
      type: Boolean,
      default: false
    },
    analyticsData: {
      type: Array,
      default: () => []
    },
    availableInStore: [Array, Boolean],
    esputnikData: Object,
    esutnikBlockId: String,
    progressbarTotal: {
      type: Number,
      default: 0
    },
    progressbarCurrent: {
      type: Number,
      default: 0
    }
  },
  methods: {
    ...mapActions('microdata', ['APPEND_PRODUCT']),
    ...mapActions('wishlist', ['CHANGE_WISH_LIST_STATUS']),
    ...mapActions('waitlist', ['CHANGE_WAIT_LIST_STATUS']),
    async handleRedirect (event) {
      const { e, link } = event
      e.preventDefault()
      const analyticsData = this.analyticsData.map(item => {
        if (item.hasOwnProperty('quantity')) delete item.quantity

        return { ...item, position: this.position }
      })
      for (let item of analyticsData) {
        if (!item.id) {
          Sentry.captureMessage('Empty analytics data item', {
            extra: { analyticsData },
            tags: { category: 'analytics' }
          })
        }
      }
      if (GoogleEnhancedEcommerce.isEnabled) {
        await GoogleEnhancedEcommerce.selectItem({
          'items': [GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(this.analyticsData[0])]
        })
      } else {
        await GoogleTagManagerService.productClickEvent({
          analyticsData,
          actionField: {
            'list': this.list || ''
          }
        })
        await GoogleTagManagerService.selectItemAnalytics({
          analyticsData: this.dataForAnalytics
        })
      }
      location.href = link
    },
    handlePhoto (e, isEnter) {
      const { setSecondaryPhoto, setMainPhoto } = this.$refs.productPhoto
      if (isEnter) setSecondaryPhoto()
      else setMainPhoto()
    },
    checkIfProductFromBannerLink () {
      const savedBanners = JSON.parse(localStorage.getItem('clickedBanners')) || []
      const currPathWithoutLang = window.location.pathname.replace('/uk/', '/')
      const currBanner = currPathWithoutLang !== '/' && savedBanners.find(el => el.banner_link.includes(currPathWithoutLang))
      if (currBanner) {
        const bannerPromoItems = JSON.parse(localStorage.getItem('bannerPromoItems')) || {}
        if (!Array.isArray(bannerPromoItems[currBanner.promotion_id])) {
          bannerPromoItems[currBanner.promotion_id] = []
        }
        if (!bannerPromoItems[currBanner.promotion_id].find(el => el.toString() === this.computedCode.toString())) {
          bannerPromoItems[currBanner.promotion_id].push(this.computedCode)
        }
        localStorage.setItem('bannerPromoItems', JSON.stringify(bannerPromoItems))
      }
    },
    addMicrodata () {
      this.APPEND_PRODUCT({
        data: getProductMicrodata(
          {
            title: this.title,
            productUrl: this.link,
            imageUrl: this.imageUrl,
            availability: this.is_available,
            price: this.getNewPrice,
            reviewCount: this.reviewsCount,
            ratingValue: this.rating,
            characteristics: this.characteristics
          }
        ),
        parentName: this.$parent.$options.name
      })
    },
    addToWishList () {
      if (!this.pending) {
        this.pending = true
        this.CHANGE_WISH_LIST_STATUS({
          id: this.id,
          code: this.code,
          price: this.getNewPrice,
          isInStock: this.inStock
        })
          .then(() => {
            if (GoogleEnhancedEcommerce.isEnabled) {
              GoogleEnhancedEcommerce.addToWishlist({
                value: +GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(this.analyticsData[0]).price * parseInt(GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(this.analyticsData[0]).quantity || 1),
                universal_value: this.isInWishList ? 'add' : 'removed',
                items: [GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(this.analyticsData[0])]
              })
            }
            this.pending = false
            this.removeFromWaitList()
          })
          .catch(error => {
            this.notifyError(error)
            this.pending = false
          })
      }
    },
    removeFromWaitList () {
      if (!this.pending) {
        let postData = {
          product_id: this.id,
          remove: 1
        }
        this.pending = true

        this.CHANGE_WAIT_LIST_STATUS({ postData: postData })
          .then(response => {
            this.pending = false
            this.notifySuccess(response.message)
          })
          .catch(error => {
            this.notifyError(error)
          })
      }
    },
    notifyError (message) {
      this.$notify({
        group: 'error',
        text: message,
        type: 'error'
      })
    },
    notifySuccess (message) {
      this.$notify({
        group: 'success',
        text: message,
        type: 'success'
      })
    }
  },
  computed: {
    ...mapState({
      config: state => state.init.config,
      userStore: state => state.geoLocation.userStore
    }),
    ...mapGetters(['isManager']),
    availableInStoreWithQuantity () {
      return Array.isArray(this.availableInStore) && this.availableInStore.find(el => +el.quantity > 0)
    },
    availableStoreId () {
      if (this.userStore && this.availableInStoreWithQuantity && this.userStore.id) {
        const store = this.availableInStore.find(el => +el.id === +this.userStore.id)
        return store ? +store.id : null
      }
      return null
    },
    // TODO: УДАЛИТЬ!
    costilForCheckSale () {
      return (this.code?.includes('-') || ([5956, 5915, 704].includes(this.id))) ?? false
    },
    dataForAnalytics () {
      const price = JSON.parse(this.price)
      const discount = parseFloat(price.price_old) - parseFloat(price.price_new)
      return {
        ...this.analyticsData[0],
        item_name: this.titleAnalytics || this.title,
        discount: discount.toFixed(2),
        index: this.position,
        item_list_name: this.block || ''
      }
    },
    parsedBadges () {
      if (!this.badges) {
        return []
      }

      const badges = Array.isArray(this.badges) ? this.badges : JSON.parse(this.badges)
      return Array.isArray(badges) ? badges : []
    },
    computedBadges () {
      // Если активны счастливые часы, переопределяем бейджи
      if (this.config?.['happy_hours']?.['is_in_time'] && this.config?.['happy_hours']?.['sale_rate'] && !this.costilForCheckSale) {
        return [{
          name: '-' + this.config.happy_hours.sale_rate + '% 🕓',
          color: '#4e93d9'
        }]
      }

      return this.parsedBadges.filter(b => b.color !== '#FF2F00')
    },
    priceObject: function () {
      return JSON.parse(this.price)
    },
    productCardWidth () {
      return this.$refs.productCard.getBoundingClientRect()
    },
    computedCode () {
      if (this.analyticsData.length > 1) {
        return this.analyticsData[0].id
      }
      return this.analyticsData[0].id || this.code
    },
    list () {
      let list

      if (window.translationParams.route.includes('site/index') && this.block === 'sale') {
        list = 'Sale Main Page'
      } else if (window.translationParams.route.includes('shop/catalog/sale')) {
        list = 'Sale Page'
      } else if (window.translationParams.route.includes('shop/catalog/novelty')) {
        list = 'New Products Page'
      } else if (window.translationParams.route.includes('shop/catalog/search')) {
        list = 'Search Page'
      } else if (window.translationParams.route.includes('site/index') && this.block === 'novelty') {
        list = 'New Products Main Page'
      } else if (window.translationParams.route.includes('shop/catalog/category')) {
        list = 'Category Page'
      } else if (window.translationParams.route.includes('shop/catalog/product') && this.block === 'up_sale') {
        list = 'Upsale Product Page'
      } else if (window.translationParams.route.includes('shop/catalog/outlet')) {
        list = 'Outlet Page'
      } else if (this.block === 'bytogether') {
        list = 's-etim-tovarom-pokupayut'
      } else if (this.block === 'bytogethercart') {
        list = 's-etim-tovarom-pokupayut-korzina'
      } else if (this.block) {
        list = this.block
      }
      return list
    },
    getNewPrice () {
      return +this.priceObject.price_new ?? null
    },
    getOldPrice () {
      return this.priceObject.price_old && this.priceObject.price_old !== this.priceObject.price_new
        ? +this.priceObject.price_old
        : null
    },
    showProductPrice () {
      return (this.isAvailable && this.getNewPrice) || (this.availableInStore && this.getNewPrice) || (this.isPreorder && this.getNewPrice)
    },
    isBuyButtonVisible () {
      // параметр inStock на данный момент (21.01.22) прокидывается только в вишлисте.
      if (this.inStock !== undefined && this.inStock === false && !this.availableInStore) {
        return false
      }
      return !this.withoutBuy && (this.isAvailable || this.availableInStore)
    },
    isWaitlistPage () {
      return this.$route?.name === 'cabinet-waitlist'
    },
    getPriceDiff () {
      const badges = this.parsedBadges
      const priceDiff = badges.find(badge => badge.color === '#FF2F00')
      return priceDiff
    },
    getCharacteristicTabUrl () {
      const characteristicTab = '?tab=characteristics'

      return `${this.link}${characteristicTab}`
    },
    showCharacteristicsLink () {
      return this.characteristics.length
    },
    images () {
      return this.gallery?.length < 1 ? Array.from({ length: 1 }, () => ({
        path: this.imageUrl
      })) : this.gallery
    }
  },
  directives: {
    lazyload: LazyLoadDirective
  },
  created () {
    this.addMicrodata()
  },
  mounted () {
    const actualProducts = this.$el.closest('.category-products-wrap__item--actual')

    if (actualProducts) {
      const skeleton = actualProducts.parentElement.querySelector('.category-products-wrap__item--skeleton')

      if (skeleton) {
        skeleton.remove()
      }

      actualProducts.classList.remove('category-products-wrap__item--actual')
      actualProducts.removeAttribute('aria-hidden')

      if (!document.querySelector('.category-products-wrap__item--skeleton')) {
        const skeletonStylesheet = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
          .find(stylesheet => stylesheet.href.includes('/css/product-card-skeleton.css'))

        if (skeletonStylesheet) {
          skeletonStylesheet.remove()
        }
      }
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.checkIfProductFromBannerLink()
          if (this.$parent && this.$parent.collectAnalyticsData) {
            if (GoogleEnhancedEcommerce.isEnabled) {
              let GEEdata = this.analyticsData[0]
              if (this.categoryBlockTitle && this.categoryBlockId) {
                GEEdata = {
                  ...GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(this.analyticsData[0]),
                  item_list_name: this.categoryBlockTitle,
                  item_list_id: this.categoryBlockId
                }
              }
              this.$parent.collectAnalyticsData(GEEdata)
            } else {
              this.$parent.collectAnalyticsData(this.dataForAnalytics)
            }
          }
          const analyticsData = this.analyticsData.map(item => {
            if (item.hasOwnProperty('quantity')) delete item.quantity

            return { ...item, position: this.position, list: this.list || '' }
          })
          if (GoogleEnhancedEcommerce.isEnabled) {
            this.$emit('onIntersecting', GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(this.analyticsData[0]))
          } else {
            this.$emit('onIntersecting', this.dataForAnalytics)
            GoogleTagManagerService.productViewEvent({ analyticsData })
          }
          // observer.unobserve(this.$refs.productCard)
        }
      },
      { threshold: 0.2 }
    )
    observer.observe(this.$refs.productCard)
    // setTimeout(() => {
    //   observer.observe(this.$refs.productCard)
    // }, 0)
  }
}
</script>

<style lang="sass" scoped>

.product-card
  position: relative
  margin-top: 20px
  padding-bottom: 15px
  border-bottom: 1px solid #ededed

  +mmin($tablet)
    height: 375px
    margin-top: 0
    padding-bottom: 0
    overflow: hidden
    border-bottom: 0

    &:hover
      overflow: visible

    &:hover .product-card__wish-list-btn,
    &:hover .product-card__add-to-comparison
      display: block

    &:hover .product-card__full
      position: relative
      z-index: 3

    &:hover .product-card__full:before
      box-shadow: 0 3px 15px rgba(0, 0, 0, 0.15)

.special-offer .owl-item:not(.active) .product-card,
.swiper-item:not(.swiper-slide-visible) .product-card,
.swiper-slide-prev .product-card

  +mmin($tablet)
    pointer-events: none

.product-card--not--adaptive
  height: 375px
  margin-top: 0
  padding-bottom: 0
  border-bottom: 0

.product-card--open
  height: auto

.product-card--slider
  height: auto

  +mmin($tablet)
    height: 375px

.product-card__full
  background-color: $page-bg

  +mmin($tablet)
    padding: 15px

    &:before
      content: ''
      position: absolute
      left: -1px
      right: -1px
      top: -1px
      bottom: 0
      background-color: $page-bg
      border-radius: 3px
      z-index: -1

.product-card--not--adaptive .product-card__full
  padding: 15px

.product-card__info
  min-height: 210px
  display: flex
  flex-flow: column wrap

.product-card--not--adaptive .product-card__info
  min-height: 375px
  margin-left: 0

.product-card__height
  margin-left: 130px
  order: -1

  +mmin($tablet)
    min-height: 290px
    margin-left: 0
    order: 0

.product-card--not--adaptive .product-card__height
  min-height: 290px
  margin-left: 0
  order: 0
  display: flex
  flex-direction: column

  +mmin($tablet)
    min-height: 290px

.product-card__reviews-wrap,
.product-card__reviews,
.product-card__price-actions
  display: flex
  align-items: center

.product-card__reviews-wrap,
.product-card__price-actions
  flex-flow: row wrap
  justify-content: space-between

.product-card__price-actions .product-card__buy-btn
  +mmin($tablet)

.product-card__reviews-wrap
  position: absolute
  left: 0
  top: 130px
  max-width: 120px

  +mmin($tablet)
    position: static
    max-width: 100%

.product-card--not--adaptive .product-card__reviews-wrap
  position: static
  max-width: 100%

.product-card__reviews
  align-items: baseline
  margin-bottom: 10px

  +mmin($tablet)
    margin-top: 5px
    margin-bottom: 0

.product-card__rating
  margin-right: 20px

  +mmin($tablet)
    margin-right: 10px

.product-card--not--adaptive .product-card__reviews
  position: static
  margin-bottom: 0

.product-card__price-actions
  min-height: 34px
  align-items: flex-end
  margin-bottom: 15px
  margin-left: 130px

  +mmin($tablet)
    margin-top: auto
    margin-left: 0
    align-items: center

.product-card--not--adaptive .product-card__price-actions
  margin-top: 0
  margin-left: 0

.product-card__price
  margin: 0

  +mmin($tablet)
    margin: 0

.product-card__price-wrap
  display: flex
  flex-direction: column
  align-items: flex-start
  margin: 0

  .product-card__price
    margin-top: 0

  +mmin($tablet)
    margin: 0

.product-card__price-diff
  margin-bottom: 5px

.product-card--not--adaptive:not(.product-card--adaptive-main-page):not(.product-card--simple-card) .product-card__price
  display: block
  margin: 5px 0

.product-card__price--not-available
  color: $placeholder-color
  font-weight: 500
  font-size: 16px
  line-height: 19px

  +mmin($tablet)
    font-size: 18px
    line-height: 21px

.product-card--not--adaptive .product-card__price--not-available
  font-size: 18px
  line-height: 21px

.product-card__price--not-available-arrival
  font-size: 16px

.product-card__price--pre-order--available
  color: $placeholder-color
  font-weight: 500
  font-size: 16px
  line-height: 19px

.product-card__arrival-date-employee
  display: block
  font-size: 12px
  line-height: 15px
  color: $main-color

.product-card__actions
  display: flex
  flex-flow: row-reverse nowrap
  align-items: center
  margin-top: auto
  justify-content: space-around

  +mmin($tablet)
    justify-content: flex-start
    flex-flow: row nowrap

.product-card__pseudo-btn
  display: flex
  align-items: center
  padding: 6px 36px
  max-height: 40px
  background-color: $main-color
  border: 1px solid $main-color
  border-radius: 3px
  color: #fff
  font-weight: 500
  font-size: 14px
  line-height: normal
  transition: background-color .1s ease-in-out,color .1s ease-in-out

  &:hover
    background-color: $main-color--hover

.product-card__actions + .product-card__characteristics
  +mmin($tablet)

.product-card--not--adaptive .product-card__actions
  justify-content: flex-start
  flex-flow: row nowrap

.product-card__characteristics
  order: -1
  margin-left: 130px
  margin-bottom: 10px

  +mmin($tablet)
    order: 0
    margin-left: 0
    margin-bottom: 0

.product-card--not--adaptive .product-card__characteristics
  display: none

  +mmin($tablet)
    display: block
    order: 0
    margin-left: 0

.wait-list
  display: none

  +mmin($tablet)
    display: block

.product-card--not--adaptive
  .wait-list
    display: block

.product-card__badges
  position: absolute
  top: 0
  left: 0

  +mmin($tablet)
    top: 20px
    left: 20px

.product-card--not--adaptive .product-card__badges
  top: 20px
  left: 20px

.product-card__wish-list-btn,
.product-card__add-to-comparison

  +mmin($tablet)
    position: absolute
    right: 20px

.product-card__add-to-comparison
  +mmin($tablet)
    top: 60px

.product-card__wish-list-btn
  +mmin($tablet)
    top: 20px

.product-card--not--adaptive
  .product-card__add-to-comparison,
  .product-card__wish-list-btn
    display: block
    position: absolute
    right: 20px

    +mmin($tablet)

  .product-card__add-to-comparison
    top: 60px

  .product-card__wish-list-btn
    top: 20px

+mmin($tablet)
  .product-card__photo-wrapper
    position: relative

.product-card--adaptive-main-page
  .product-card__photo-wrapper
    +mmax($tablet)
      position: relative

  .product-card__photo
    +mmax($tablet)
      position: absolute
      left: 0
      top: 0
      width: 100%

.product-card__bottom-badges
  +mmin($tablet)
    position: absolute
    bottom: 0
    left: 0
    pointer-events: none

.product-card--is-touch
  .product-card__add-to-comparison,
  .product-card__wish-list-btn

    +mmin($tablet)
      display: block

.product-card--not--adaptive .product-card__bottom-badges
  position: absolute
  bottom: 0
  left: 0
  pointer-events: none

.product-card__code--hidden
  opacity: 0

.product-card--adaptive-main-page
  +mmax(768)
    height: auto

    .product-card__pseudo-btn
      display: none

    .product-card__height
      width: 100%
      min-height: auto

    .product-card__info
      min-height: auto

    .product-card__bottom-badges
      display: none

    .product-card__code
      display: none

    .product-card__options
      display: none

    .product-card__characteristics
      display: none

    .product-card__actions
      .product-card__buy-btn
        display: none

      .wait-list--card
        display: none

      .product-card__add-to-comparison
        top: 50px
        right: 10px

      .product-card__wish-list-btn
        top: 10px
        right: 10px

    .product-card__price-actions
      width: 100%
      align-items: center
      margin: 15px 0 0

    .product-card__badges
      //display: none
      top: 10px
      left: 10px

.product-card--simple-card
  height: auto

  &:hover
    overflow: hidden

    .product-card__full
      position: initial
      z-index: 0

  .product-card__pseudo-btn
    display: none

  .product-card__full
    border: 1px solid $nd-border-color
    padding: 15px 10px

    &:hover:before
      box-shadow: none

  .product-card__photo
    width: 100%
    max-width: 140px
    margin: 0 auto
    min-height: 150px

    +mmin($tablet)
      max-width: 169px
      min-height: 200px

  .product-card__height
    width: 100%
    min-height: auto

  .product-card__info
    min-height: auto

  .product-card__bottom-badges
    display: none

  .product-card__code
    display: none

  .product-card__options
    display: none

  .product-card__characteristics
    display: none

  .product-card__actions
    .product-card__buy-btn
      display: none

    .wait-list--card
      display: none

    .product-card__add-to-comparison
      top: 50px
      right: 10px

    .product-card__wish-list-btn
      top: 10px
      right: 10px

  .product-card__price-actions
    width: 100%
    align-items: center
    margin: 15px 0 0

    .product-card__buy-btn
      display: block

  .product-card__badges
    //display: none
    top: 10px
    left: 10px

  .product-card__title
    min-height: 35px

.product-card__wait-list-btn
  position: absolute
  right: 20px
  top: 20px
  border-radius: 3px
  padding: 5px
  width: 30px
  height: 30px
  &:hover
    background-color: #E7E7E7

.product-card:hover
  .product-card__wait-list-btn
    opacity: 1
    pointer-events: auto

@media screen and (min-width: 1025px)
  .product-card__wait-list-btn
    opacity: 0
    pointer-events: none

</style>

<style lang="scss" scoped>
.product-card {
  &__photo-wrapper {
    margin-bottom: 10px;
  }

  &__price-diff {
    margin-left: 10px;
  }

  &__badges {
    z-index: 1;
  }

  &__gallery {
    margin: 0 auto;
    height: 200px;
  }

  &__code {
    font-size: 12px;
    line-height: 14px;
    color: #909090;
    margin-top: 5px;
    margin-bottom: -5px;
    display: flex;
    align-items: center;
  }

  &__code-copy {
    width: 18px;
    height: 18px;
    border: 0;
    padding: 0;
    background: transparent !important;
    margin-left: 5px;

    &:focus {
      fill: red;
    }
  }

  &__actions {
    margin-bottom: 0;
    z-index: 1;

    .product-card__buy-btn {
      display: none;
    }
  }

  &__add-to-comparison {
    background: #FFFFFFD9;
    ::v-deep .add-to-comparison__svg {
      fill: #909090;
      width: 20px;
      height: 18px;
    }
  }

  &__wish-list-btn {
    background: #FFFFFFD9;
    ::v-deep {
      .wish-list-btn__svg {
        stroke: #909090;
        width: 18px;
        height: 16px;
      }
    }
  }

  &__wait-list {
    ::v-deep {
      .wait-list__btn {
        width: 52px;
        height: 52px;
        min-width: 100%;
        background-color: transparent;
        border-radius: 3px;
        border: 1px solid #ccc;
      }
      .wait-list__btn-img {
        display: block;
        margin: 0;
      }
      .wait-list__btn-img-added {
        display: block;
      }
      .wait-list__btn-title {
        display: none;
      }
    }
  }

  &__add-to-comparison,
  &__wish-list-btn {
    &:hover {
      background-color: hsla(0, 0%, 97.3%, .8509803921568627) !important;
      cursor: pointer;
      ::v-deep .wish-list-btn__svg {
        stroke: #909090;
      }
      ::v-deep .add-to-comparison__svg {
        fill: #909090;
      }
    }
  }

  &__buy-btn {
    ::v-deep {
      .buy-btn {
        width: 52px;
        height: 52px;
        max-height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 0;
        .buy-btn__svg {
          width: 24px;
          height: 24px;
        }
      }
    }
  }

  &__characteristics-link {
    display: inline-block;
    text-decoration: underline;
    color: #3E77AA;
    font-size: 14px;
    line-height: 18px;
    margin-top: 20px;
  }

  &__options {
    padding: 15px 15px 7px;
    background: #F8F8F8;
    margin: 3px 0 0 !important;
    ::v-deep {
      .product-options {
        margin: 0 !important;
      }
    }
  }

  @media (max-width: 1024px) {

  }

  @media (max-width: 768px) {
    height: 328px;
    overflow: hidden;

    &__full {
      height: 100% !important;
    }

    &__gallery {
      width: 140px;
      height: 140px;
    }

    &__price {
      &--not-available {
        font-size: 14px !important;
        line-height: 18px !important;
      }
    }

    &__price-actions {
      flex-flow: row;
    }

    &__buy-btn {
      ::v-deep {
        .buy-btn {
          width: 42px;
          height: 42px;
          .buy-btn__svg {
            width: 20px;
            height: 20px;
          }
          .add-to-cart__checkmark {
            width: 14px;
            height: 14px;
          }
        }
      }
    }

    &__wait-list {
      ::v-deep {
        .wait-list__btn {
          width: 42px;
          height: 42px;
          .wait-list__btn-img {
            width: 20px;
            height: 20px;
          }
          .wait-list__btn-img-added {
            width: 14px;
            height: 14px;
          }
        }
      }
    }

    &__characteristics,
    &__characteristics-link {
      display: none;
    }

  }
}
</style>
